/**
 * Widget registry (Beta-1 plan P3-15) — the catalogue every dashboard draws from.
 *
 * A widget is a SHORTCUT: "the widgets basically are just representative of something that
 * has its own page" (Tim, C0813). Each definition names the page it stands for (`href`), the
 * sizes it supports, who may see it (`minRole`, refined by the P3-21 matrix) and the settings
 * a user can change per instance (`settingsSchema`, rendered by P3-18).
 *
 * Platform-level (plan §1c): the same registry serves the REPSit dashboard, DREW's OE Dashboard
 * and HUGO later — `dashboards` restricts a widget to some of them.
 *
 * Pure module (components are opaque values) so it can be unit-tested with `node --test`.
 */
import { normalizeSizes } from './sizes.js';
import { ROLE_HIERARCHY, hasMinimumRole } from '../../services/roleHierarchy.js';
import { roleMeetsWidget } from './widgetAccess.js';

/** Gallery categories (P3-17), in display order. Keys are stored in definitions; values are labels. */
export const WIDGET_CATEGORIES = Object.freeze({
  scores: 'Scores',
  alerts: 'Alerts',
  oe_conditions: 'OE Conditions',
  surveys: 'Surveys',
  activity: 'Activity',
  library: 'Library',
  guidance: 'Guidance',
});
export const CATEGORY_ORDER = Object.freeze(Object.keys(WIDGET_CATEGORIES));

/** Dashboards the framework runs (P3-19 `dashboardKey`). */
export const DASHBOARD_KEYS = Object.freeze({
  repsit: 'repsit', // REPSit welcome dashboard (/dashboard) — the platform's main-menu Dashboard
  drewOe: 'drew-oe', // DREW's OE Dashboard (/drew)
  // DREW's ACCESS LEVELS role dashboards — each its own customizable dashboard (Alex, 09-12)
  drewItm: 'drew-itm', // Individual Contributor (/drew/role/itm)
  drewFls: 'drew-fls', // Frontline Supervisor / Team Leader (/role/fls)
  drewDh: 'drew-dh', // Department Head (/role/dh)
  drewExec: 'drew-exec', // Executive / Site Manager (/role/executive)
});

/** Field types a settingsSchema may use (P3-18 renders them). */
export const SETTING_FIELD_TYPES = Object.freeze(['select', 'toggle', 'period', 'scope', 'text']);

const ID_RE = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

function fail(id, msg) {
  throw new Error(`defineWidget(${id ?? '?'}): ${msg}`);
}

function validateSettingsSchema(id, schema) {
  if (!Array.isArray(schema)) fail(id, 'settingsSchema must be an array');
  const keys = new Set();
  return schema.map((field) => {
    if (!field || typeof field !== 'object') fail(id, 'settingsSchema entries must be objects');
    const { key, label, type, options, default: def, ...rest } = field;
    if (typeof key !== 'string' || !key) fail(id, 'settings field needs a string key');
    if (keys.has(key)) fail(id, `duplicate settings key "${key}"`);
    keys.add(key);
    if (typeof label !== 'string' || !label) fail(id, `settings field "${key}" needs a label`);
    if (!SETTING_FIELD_TYPES.includes(type)) fail(id, `settings field "${key}" has unknown type "${type}"`);
    if (type === 'select' && (!Array.isArray(options) || options.length === 0)) {
      fail(id, `settings field "${key}" (select) needs options`);
    }
    return Object.freeze({ key, label, type, options: options ? Object.freeze([...options]) : undefined, default: def, ...rest });
  });
}

/**
 * Validate and freeze a widget definition.
 *
 * @param {object} def
 * @param {string}   def.id             kebab-case, unique
 * @param {string}   def.title          card title (what testers see)
 * @param {string}   def.category       key of WIDGET_CATEGORIES
 * @param {*}        def.component      React component rendering the body: ({ item, size, props, editing, cols })
 * @param {string[]} def.sizes          allowed size keys (S/M/L/XL), any order
 * @param {string}  [def.defaultSize]   one of `sizes` (default: the smallest allowed)
 * @param {string}  [def.minRole]       stored role key the viewer must meet (default 'respondent')
 * @param {string|function} [def.href]  page the widget stands for; function receives (props, item)
 * @param {object[]} [def.settingsSchema] per-instance settings (P3-18)
 * @param {string}  [def.description]   one line for the gallery (P3-17)
 * @param {*}       [def.preview]       optional preview component for the gallery
 * @param {function} [def.availability] extra predicate (ctx) → boolean, on top of minRole
 * @param {string[]} [def.dashboards]   restrict to some DASHBOARD_KEYS (default: all)
 * @param {boolean} [def.frameless]     the component brings its own card/heading (today's dashboard
 *                                      sections, P3-20): the shell draws no header, only the edit-mode
 *                                      overlays (remove badge, size caption, gear)
 * @param {boolean} [def.clickable]    the whole card is the shortcut at every size (default: only Small)
 * @param {boolean} [def.scroll]        body scrolls instead of clipping when the content is taller
 *                                      than the cell (lists, breakdowns)
 */
export function defineWidget(def) {
  if (!def || typeof def !== 'object') throw new TypeError('defineWidget: a definition object is required');
  const {
    id,
    title,
    category,
    component,
    sizes,
    defaultSize,
    minRole = 'respondent',
    href = null,
    settingsSchema = [],
    description = '',
    preview = null,
    availability = null,
    dashboards = null,
    frameless = false,
    scroll = false,
    clickable = null,
  } = def;

  if (typeof id !== 'string' || !ID_RE.test(id)) fail(id, 'id must be kebab-case (a-z, 0-9, dashes)');
  if (typeof title !== 'string' || !title.trim()) fail(id, 'title is required');
  if (!Object.prototype.hasOwnProperty.call(WIDGET_CATEGORIES, category)) {
    fail(id, `unknown category "${category}" (expected one of ${CATEGORY_ORDER.join(', ')})`);
  }
  if (component == null) fail(id, 'component is required');
  const allowed = normalizeSizes(sizes);
  if (allowed.length === 0) fail(id, 'sizes must contain at least one of S, M, L, XL');
  const size0 = defaultSize ?? allowed[0];
  if (!allowed.includes(size0)) fail(id, `defaultSize "${size0}" is not in sizes [${allowed.join(', ')}]`);
  if (!Object.prototype.hasOwnProperty.call(ROLE_HIERARCHY, minRole)) fail(id, `unknown minRole "${minRole}"`);
  if (href != null && typeof href !== 'string' && typeof href !== 'function') fail(id, 'href must be a string or a function');
  if (availability != null && typeof availability !== 'function') fail(id, 'availability must be a function');
  if (dashboards != null) {
    if (!Array.isArray(dashboards) || dashboards.length === 0) fail(id, 'dashboards must be a non-empty array');
    const known = Object.values(DASHBOARD_KEYS);
    for (const d of dashboards) if (!known.includes(d)) fail(id, `unknown dashboard "${d}"`);
  }

  return Object.freeze({
    id,
    title: title.trim(),
    description: typeof description === 'string' ? description : '',
    category,
    component,
    sizes: Object.freeze(allowed),
    defaultSize: size0,
    minRole,
    href,
    settingsSchema: Object.freeze(validateSettingsSchema(id, settingsSchema)),
    preview,
    availability,
    dashboards: dashboards ? Object.freeze([...dashboards]) : null,
    frameless: Boolean(frameless),
    scroll: Boolean(scroll),
    clickable: clickable == null ? null : Boolean(clickable),
  });
}

/**
 * May this widget be shown to a viewer? `ctx.role` is a stored role key; a null/undefined
 * role means "not resolved yet" and does NOT hide anything (UI-level gating only — the same
 * convention as accessControl.js; real security is firestore.rules). The P3-21 matrix
 * refines this per dashboard.
 */
export function isWidgetAllowed(widget, ctx = {}) {
  if (!widget) return false;
  const { role = null, dashboard = null } = ctx;
  if (dashboard && widget.dashboards && !widget.dashboards.includes(dashboard)) return false;
  if (role != null && widget.minRole && !hasMinimumRole(role, widget.minRole)) return false;
  if (!roleMeetsWidget(role, widget)) return false; // P3-21 matrix (ACCESS_OVERRIDES)
  if (widget.availability && !widget.availability(ctx)) return false;
  return true;
}

/** Resolve the page a widget instance stands for (`null` when it has none). */
export function widgetHref(widget, item = null) {
  if (!widget || widget.href == null) return null;
  return typeof widget.href === 'function' ? widget.href(item?.props || {}, item) : widget.href;
}

/** A registry instance. The app uses the singleton `widgetRegistry`; tests create their own. */
export function createWidgetRegistry() {
  const map = new Map();
  return {
    /** Validate + add a definition. Throws on duplicates and invalid definitions. */
    register(def) {
      const widget = defineWidget(def);
      if (map.has(widget.id)) throw new Error(`Widget "${widget.id}" is already registered`);
      map.set(widget.id, widget);
      return widget;
    },
    /** Register or replace (Vite HMR re-runs module bodies; dev only). */
    upsert(def) {
      const widget = defineWidget(def);
      map.set(widget.id, widget);
      return widget;
    },
    unregister(id) {
      return map.delete(id);
    },
    get(id) {
      return map.get(id) || null;
    },
    has(id) {
      return map.has(id);
    },
    ids() {
      return [...map.keys()];
    },
    /** Definitions, optionally filtered by category / dashboard / viewer role, in category order. */
    list({ category = null, dashboard = null, role = undefined } = {}) {
      let arr = [...map.values()];
      if (category) arr = arr.filter((w) => w.category === category);
      if (dashboard || role !== undefined) arr = arr.filter((w) => isWidgetAllowed(w, { role, dashboard }));
      return arr.sort(
        (a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category) || a.title.localeCompare(b.title)
      );
    },
    clear() {
      map.clear();
    },
    get size() {
      return map.size;
    },
  };
}

/** The platform-wide registry. Widgets register themselves by importing this and calling `register`. */
export const widgetRegistry = createWidgetRegistry();

export function registerWidget(def) {
  // In dev, Vite HMR re-executes widget modules; replacing keeps the page alive instead of throwing.
  const hot = typeof import.meta !== 'undefined' && import.meta.hot;
  return hot ? widgetRegistry.upsert(def) : widgetRegistry.register(def);
}
